import React from 'react';
import { Box, Card, CardContent, Typography, Button, Chip } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import type { EntityActionConfig } from '@/types/Entity';

interface EntityFilterBarProps {
  title?: string;
  entityType?: string;
  entityCount?: number;
  topLevelCount?: number;
  actions?: EntityActionConfig[];
  onActionClick?: (action: EntityActionConfig) => void;
  onExport?: () => void;
  activeFilters?: Array<[string, string]>;
  onClearFilters?: () => void;
  children?: React.ReactNode;
}

export const EntityFilterBar: React.FC<EntityFilterBarProps> = ({
  title,
  entityType,
  entityCount,
  topLevelCount,
  actions = [],
  onActionClick,
  onExport,
  activeFilters = [],
  onClearFilters,
  children,
}) => {
  const formatFilterKey = (key: string) => {
    if (key === 'or') return 'search';
    if (key === 'display_name') return 'search';
    if (key === 'entity_data->>category') return 'Category';
    if (key === 'status') return 'Status';
    return key.replace('entity_data->>', '').replace(/_/g, ' ');
  };

  const formatFilterValue = (key: string, value: string) => {
    if (key === 'or') {
      const orSearchMatch = value.match(/\*([^*]+)\*/);
      if (orSearchMatch?.[1]) {
        return orSearchMatch[1].trim();
      }
    }

    const ilikeWrappedMatch = value.match(/^ilike\.\*(.*)\*$/i);
    if (ilikeWrappedMatch?.[1] !== undefined) {
      return ilikeWrappedMatch[1].trim();
    }

    if (value.startsWith('eq.')) return value.slice(3);
    if (value.startsWith('ilike.')) return value.slice(6);
    return value;
  };

  return (
    <Card sx={{ mb: 1 }}>
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: activeFilters.length ? 1 : 0,
          }}
        >
          <Box>
            <Typography variant="subtitle1" component="h2">
              {title ||
                (entityType
                  ? `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} Entities`
                  : 'Entities')}
            </Typography>
            {entityCount !== undefined && (
              <Typography variant="body2" color="text.secondary">
                {topLevelCount !== undefined && topLevelCount !== entityCount
                  ? <>{topLevelCount} top-level {topLevelCount === 1 ? 'item' : 'items'} ({entityCount} total including sized variants)</>
                  : <>{entityCount} {entityCount === 1 ? 'entity' : 'entities'} found</>
                }
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {children}
            {actions.map((action) => (
              <Button
                key={action.id}
                variant="contained"
                size="small"
                onClick={() => onActionClick?.(action)}
              >
                {action.label}
              </Button>
            ))}
            {onExport && (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={onExport}
                size="small"
              >
                Export CSV
              </Button>
            )}
          </Box>
        </Box>

        {activeFilters.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Active Filters:
            </Typography>
            {activeFilters.map(([key, value]) => (
              <Chip
                key={`${key}:${value}`}
                label={`${formatFilterKey(key)}: ${formatFilterValue(key, value)}`}
                size="small"
                color="primary"
              />
            ))}
            {onClearFilters && (
              <Button variant="text" size="small" onClick={onClearFilters} sx={{ ml: 'auto' }}>
                Clear Filters
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
